import {
  calculatePartialDateDiffenceDuration,
  isPartialDate,
  partialDateFromDate,
  partialDateToValue,
  sortPartialDate,
} from 'neuro-utils';
import { filterByDocumentType } from 'Utility/documentHandling';
import { defineDocConfig } from './utils';

import type { ClinicalArchive } from '.';

export const ninmtDocumentTypes = defineDocConfig([
  {
    name: 'diagnosis',
    subTypes: [
      'diagnosis',
      'ninmtTreatmentPeriod',
      'indicationForTreatment',
      'contraIndicationsToTreatment',
      'symptomsDetails',
      'symptomsAndLocations',
      'referringPhysicianAndUnit',
    ],
    title: 'basicTreatmentInformation',
  },
  {
    name: 'doctorsOrders',
    subTypes: ['doctorsOrders', 'doctorsOrdersAdditional'],
    secondaryDocs: ['ninmtTreatmentPeriod'],
  },
  {
    name: 'notes',
    subTypes: ['notes'],
    secondaryDocs: ['rtms', 'tdcs', 'doctorsOrders', 'ninmtTreatmentPeriod', 'doctorsOrdersAdditional'],
  },
  { name: 'interviewSurveys', subTypes: ['madrs', 'gaf'] },
  { name: 'medication', subTypes: ['medication', 'adverseEffect'] },
  {
    name: 'rtms',
    subTypes: ['rtms'],
    secondaryDocs: ['doctorsOrders', 'ninmtTreatmentPeriod', 'contraIndicationsToTreatment', 'symptomsAndLocations'],
  },
  {
    name: 'tdcs',
    subTypes: ['tdcs'],
    secondaryDocs: ['doctorsOrders', 'ninmtTreatmentPeriod', 'contraIndicationsToTreatment', 'symptomsAndLocations'],
  },
  { name: 'comorbidity', subTypes: ['comorbidity'], title: 'diagnoses' },
  { name: 'background', subTypes: ['background', 'measurement'] },
  { name: 'patientFiles' }, // Using only files here as name is not allowed
  {
    name: 'myService',
    title: 'titleShort',
    subTypes: [],
    secondaryDocs: ['doctorsOrders', 'rtms', 'tdcs', 'symptomsAndLocations', 'ninmtTreatmentPeriod'],
  },
]);

export const ninmtGraphDocTypes = [
  'diagnosis',
  'measurement',
  'ninmtTreatmentPeriod',
  'medication',
  'adverseEffect',
  'doctorsOrders',
  'rtms',
  'madrs',
  'gaf',
  'tdcs',
  'symptomsAndLocations',
  'contraIndicationsToTreatment',
  'comorbidity',
] satisfies TDocumentType[];

export const ninmtAlerts: IAlert[] = [
  {
    // Notification to the user if the date of the latest treatment decision/period is after the date of the latest doctor's order .
    content: 'alerts.ninmt.treatmentDecisionDateAfterLatestDoctorsOrderDate',
    id: 'ninmtTreatmentDecisionDateAfterLatestDoctorsOrderDate',
    severity: 'warning',
    popup: true,
    rule: (docs: IDocumentV2[] | undefined = []): boolean => {
      const treatmentPeriodDocs = filterByDocumentType(docs, 'ninmtTreatmentPeriod');
      const latestTreatmentPeriod = treatmentPeriodDocs.sort((a, b) => sortPartialDate(b.date, a.date)).at(0);
      const latestTreatmentPeriodDate = latestTreatmentPeriod?.date;

      const doctorsOrdersDocs = filterByDocumentType(docs, 'doctorsOrders');
      const latestDoctorsOrder = doctorsOrdersDocs.sort((a, b) => sortPartialDate(b.date, a.date)).at(0);
      const latestDoctorsOrderDate = latestDoctorsOrder?.date;

      if (!isPartialDate(latestTreatmentPeriodDate) || !isPartialDate(latestDoctorsOrderDate)) return false;

      return sortPartialDate(latestTreatmentPeriodDate, latestDoctorsOrderDate) > 0;
    },
  },
  {
    // Notification to the user if 30 days or more have passed since the contraindications were defined.
    content: 'alerts.ninmt.contraIndicationsMoreThan30DaysPassed',
    id: 'ninmtContraIndicationsMoreThan30DaysPassed',
    severity: 'warning',
    popup: true,
    rule: (docs: IDocumentV2[] | undefined = []): boolean => {
      const contraIndDocs = filterByDocumentType(docs, 'contraIndicationsToTreatment');
      const latestContraIndication = contraIndDocs.sort((a, b) => sortPartialDate(b.date, a.date)).at(0);
      const latestContraIndicationDate = latestContraIndication?.date;
      if (!isPartialDate(latestContraIndicationDate)) return false;
      const duration = calculatePartialDateDiffenceDuration('days', latestContraIndicationDate);
      return Math.abs(duration.days) >= 30;
    },
  },
  {
    // Notification to the user when patient has filled a new ContraIndicationsInquiry document
    content: 'alerts.ninmt.contraIndicationsInquiry',
    id: 'ninmtContraIndicationsInquiry',
    severity: 'warning',
    popup: true,
    rule: (docs: IDocumentV2[] | undefined = [], myAppDocs: IDocumentV2[] | undefined = []): boolean => {
      const contraIndDocs = filterByDocumentType(docs, 'contraIndicationsToTreatment').filter(
        d => !d._editing || (d._commitsLength ?? 0) > 1,
      );
      const latestContraIndication = contraIndDocs.sort((a, b) => sortPartialDate(b.date, a.date)).at(0);
      const latestContraIndicationDate = latestContraIndication?.date;

      const contraIndInquiries = filterByDocumentType(myAppDocs, 'contraIndicationsInquiry');
      const sortedContraIndInquiries = contraIndInquiries.sort((a, b) => b._docCreateDate - a._docCreateDate);
      return sortedContraIndInquiries.some(
        cii =>
          partialDateToValue(partialDateFromDate(new Date(cii._docCreateDate))) >
          partialDateToValue(latestContraIndicationDate),
      );
    },
  },
];

export const ninmtClinicalArchiveTypes: readonly ClinicalArchive[] = ['RADU'] as const;
